<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import SettingsPageLayout from 'dashboard/components-next/ailab/SettingsPageLayout.vue';
import SettingsHeader from 'dashboard/components-next/ailab/pageComponents/settings/SettingsHeader.vue';
import SuggestedRules from 'dashboard/components-next/ailab/assistant/SuggestedRules.vue';
import AddNewRulesInput from 'dashboard/components-next/ailab/assistant/AddNewRulesInput.vue';
import AddNewRulesDialog from 'dashboard/components-next/ailab/assistant/AddNewRulesDialog.vue';
import RuleCard from 'dashboard/components-next/ailab/assistant/RuleCard.vue';
import BulkSelectBar from 'dashboard/components-next/ailab/assistant/BulkSelectBar.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();

const assistantId = route.params.assistantId;
const uiFlags = useMapGetter('ailabAssistants/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingItem);
const assistant = computed(() =>
  store.getters['ailabAssistants/getRecord'](Number(assistantId))
);

const searchQuery = ref('');
const newInlineRule = ref('');
const newDialogRule = ref('');

const breadcrumbItems = computed(() => {
  return [
    {
      label: t('AILAB.ASSISTANTS.SETTINGS.BREADCRUMB.ASSISTANT'),
      routeName: 'ailab_assistants_index',
    },
    { label: assistant.value?.name, routeName: 'ailab_assistants_edit' },
    { label: t('AILAB.ASSISTANTS.GUARDRAILS.BREADCRUMB.TITLE') },
  ];
});

const guardrailsContent = computed(() => assistant.value?.guardrails || []);

const displayGuardrails = computed(() =>
  guardrailsContent.value.map((c, idx) => ({ id: idx, content: c }))
);

const guardrailsExample = [
  {
    id: 1,
    content:
      'Block queries that share or request sensitive personal information (e.g. phone numbers, passwords).',
  },
  {
    id: 2,
    content:
      'Reject queries that include offensive, discriminatory, or threatening language.',
  },
  {
    id: 3,
    content:
      'Deflect when the assistant is asked for legal or medical diagnosis or treatment.',
  },
];

const filteredGuardrails = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return displayGuardrails.value;
  return picoSearch(displayGuardrails.value, query, ['content']);
});

const shouldShowSuggestedRules = computed(() => {
  return uiSettings.value?.show_guardrails_suggestions !== false;
});

const closeSuggestedRules = () => {
  updateUISettings({ show_guardrails_suggestions: false });
};

// Bulk selection & hover state
const bulkSelectedIds = ref(new Set());
const hoveredCard = ref(null);

const handleRuleSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const handleRuleHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const buildSelectedCountLabel = computed(() => {
  const count = displayGuardrails.value.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('AILAB.ASSISTANTS.GUARDRAILS.BULK_ACTION.UNSELECT_ALL', { count })
    : t('AILAB.ASSISTANTS.GUARDRAILS.BULK_ACTION.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('AILAB.ASSISTANTS.GUARDRAILS.BULK_ACTION.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const saveGuardrails = async list => {
  await store.dispatch('ailabAssistants/update', {
    id: assistantId,
    assistant: { guardrails: list },
  });
};

const addGuardrail = async content => {
  try {
    const newGuardrails = [...guardrailsContent.value, content];
    await saveGuardrails(newGuardrails);
    useAlert(t('AILAB.ASSISTANTS.GUARDRAILS.API.ADD.SUCCESS'));
  } catch (error) {
    useAlert(t('AILAB.ASSISTANTS.GUARDRAILS.API.ADD.ERROR'));
  }
};

const editGuardrail = async ({ id, content }) => {
  try {
    const updated = [...guardrailsContent.value];
    updated[id] = content;
    await saveGuardrails(updated);
    useAlert(t('AILAB.ASSISTANTS.GUARDRAILS.API.UPDATE.SUCCESS'));
  } catch {
    useAlert(t('AILAB.ASSISTANTS.GUARDRAILS.API.UPDATE.ERROR'));
  }
};

const deleteGuardrail = async id => {
  try {
    const updated = guardrailsContent.value.filter((_, idx) => idx !== id);
    await saveGuardrails(updated);
    useAlert(t('AILAB.ASSISTANTS.GUARDRAILS.API.DELETE.SUCCESS'));
  } catch {
    useAlert(t('AILAB.ASSISTANTS.GUARDRAILS.API.DELETE.ERROR'));
  }
};

const bulkDeleteGuardrails = async () => {
  try {
    if (bulkSelectedIds.value.size === 0) return;
    const updated = guardrailsContent.value.filter(
      (_, idx) => !bulkSelectedIds.value.has(idx)
    );
    await saveGuardrails(updated);
    bulkSelectedIds.value.clear();
    useAlert(t('AILAB.ASSISTANTS.GUARDRAILS.API.DELETE.SUCCESS'));
  } catch {
    useAlert(t('AILAB.ASSISTANTS.GUARDRAILS.API.DELETE.ERROR'));
  }
};

const addAllExample = () => {
  updateUISettings({ show_guardrails_suggestions: false });
  try {
    const exampleContents = guardrailsExample.map(example => example.content);
    const newGuardrails = [...guardrailsContent.value, ...exampleContents];
    saveGuardrails(newGuardrails);
  } catch {
    useAlert(t('AILAB.ASSISTANTS.GUARDRAILS.API.ADD.ERROR'));
  }
};
</script>

<template>
  <SettingsPageLayout
    :breadcrumb-items="breadcrumbItems"
    :is-fetching="isFetching"
  >
    <template #body>
      <div class="flex flex-col gap-6">
        <SettingsHeader
          :heading="$t('AILAB.ASSISTANTS.GUARDRAILS.TITLE')"
          :description="$t('AILAB.ASSISTANTS.GUARDRAILS.DESCRIPTION')"
        />

        <div v-if="shouldShowSuggestedRules" class="flex flex-col gap-4">
          <SuggestedRules
            :title="$t('AILAB.ASSISTANTS.GUARDRAILS.ADD.SUGGESTED.TITLE')"
            :items="guardrailsExample"
            @add="addAllExample"
            @close="closeSuggestedRules"
          >
            <template #default="{ item }">
              <div class="flex items-center justify-between w-full">
                <span class="text-sm text-slate-900">
                  {{ item.content }}
                </span>
                <Button
                  :label="
                    $t('AILAB.ASSISTANTS.GUARDRAILS.ADD.SUGGESTED.ADD_SINGLE')
                  "
                  variant="outline"
                  color="slate"
                  size="xs"
                  class="flex-shrink-0"
                  @click="addGuardrail(item.content)"
                />
              </div>
            </template>
          </SuggestedRules>
        </div>

        <div
          class="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col gap-4"
        >
          <div
            class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3"
          >
            <BulkSelectBar
              v-model="bulkSelectedIds"
              :all-items="displayGuardrails"
              :select-all-label="buildSelectedCountLabel"
              :selected-count-label="selectedCountLabel"
              :delete-label="
                $t(
                  'AILAB.ASSISTANTS.GUARDRAILS.BULK_ACTION.BULK_DELETE_BUTTON'
                )
              "
              @bulk-delete="bulkDeleteGuardrails"
            >
              <template #default-actions>
                <AddNewRulesDialog
                  v-model="newDialogRule"
                  :placeholder="
                    t('AILAB.ASSISTANTS.GUARDRAILS.ADD.NEW.PLACEHOLDER')
                  "
                  :button-label="
                    t('AILAB.ASSISTANTS.GUARDRAILS.ADD.NEW.TITLE')
                  "
                  :confirm-label="
                    t('AILAB.ASSISTANTS.GUARDRAILS.ADD.NEW.CREATE')
                  "
                  :cancel-label="
                    t('AILAB.ASSISTANTS.GUARDRAILS.ADD.NEW.CANCEL')
                  "
                  @add="addGuardrail"
                />
                <!-- Will enable this feature in future -->
                <!-- <div class="h-4 w-px bg-n-strong" />
              <Button
                :label="t('AILAB.ASSISTANTS.GUARDRAILS.ADD.NEW.TEST_ALL')"
                xs
                ghost
                slate
                class="!text-sm"
              /> -->
              </template>
            </BulkSelectBar>
            <div
              v-if="displayGuardrails.length && bulkSelectedIds.size === 0"
              class="w-full sm:max-w-[22.5rem] min-w-0"
            >
              <Input
                v-model="searchQuery"
                :placeholder="
                  t('AILAB.ASSISTANTS.GUARDRAILS.LIST.SEARCH_PLACEHOLDER')
                "
              />
            </div>
          </div>
          <div v-if="displayGuardrails.length === 0" class="mt-1 mb-2">
            <span class="text-slate-600 text-sm">
              {{ t('AILAB.ASSISTANTS.GUARDRAILS.EMPTY_MESSAGE') }}
            </span>
          </div>
          <div v-else-if="filteredGuardrails.length === 0" class="mt-1 mb-2">
            <span class="text-slate-600 text-sm">
              {{ t('AILAB.ASSISTANTS.GUARDRAILS.SEARCH_EMPTY_MESSAGE') }}
            </span>
          </div>
          <div v-else class="flex flex-col gap-2">
            <RuleCard
              v-for="guardrail in filteredGuardrails"
              :id="guardrail.id"
              :key="guardrail.id"
              :content="guardrail.content"
              :is-selected="bulkSelectedIds.has(guardrail.id)"
              :selectable="
                hoveredCard === guardrail.id || bulkSelectedIds.size > 0
              "
              @select="handleRuleSelect"
              @edit="editGuardrail"
              @delete="deleteGuardrail"
              @hover="isHovered => handleRuleHover(isHovered, guardrail.id)"
            />
          </div>
          <AddNewRulesInput
            v-model="newInlineRule"
            :placeholder="
              t('AILAB.ASSISTANTS.GUARDRAILS.ADD.SUGGESTED.PLACEHOLDER')
            "
            :label="t('AILAB.ASSISTANTS.GUARDRAILS.ADD.SUGGESTED.SAVE')"
            @add="addGuardrail"
          />
        </div>
      </div>
    </template>
  </SettingsPageLayout>
</template>
